import { type ReactNode, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useInView, useReducedMotion } from 'framer-motion';
import { ArrowUp, Check, ChevronRight, Copy, ExternalLink, Link2, Loader2, Plug, ShieldCheck, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { MCP_CLIENTS, MCP_SCENES, type McpClient, type Scene } from '@/data/mcpContent';
import { EASE, ScoreRing } from '@/components/home/primitives';

/*
 * Illustrative chat between a recruiter and their AI assistant with Beatview connected.
 * Server render and first client render show scene 1 complete; the loop only starts once
 * the window scrolls into view, so pre-built HTML and hydration match.
 */

type Phase = 'type' | 'tools' | 'reply' | 'confirm' | 'done';

const CHAR_MS = 22;
const TOOL_MS = 750;
const HOLD_MS = 5200;

/** Roughly how long a scene runs from the first keystroke to the hand-off, for the progress bar. */
function sceneDuration(scene: Scene) {
  const typing = (scene.prompt.length / 2) * CHAR_MS + 350;
  const tools = scene.tools.length * TOOL_MS + 250;
  return typing + tools + (scene.card === 'confirm' ? 2300 : 0) + HOLD_MS;
}

function nextPhase(scene: Scene, phase: Phase): Phase | null {
  if (phase === 'type') return scene.card === 'confirm' ? 'reply' : 'tools';
  if (phase === 'tools') return scene.card === 'confirm' ? 'done' : 'reply';
  if (phase === 'reply') return scene.card === 'confirm' ? 'confirm' : null;
  if (phase === 'confirm') return 'tools';
  return null;
}

function Avatar({ initials, tone }: { initials: string; tone: string }) {
  return <span className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-bold', tone)}>{initials}</span>;
}

const people = {
  aisha: { name: 'Aisha Rahman', initials: 'AR', tone: 'bg-bv-success-soft text-bv-success' },
  maya: { name: 'Maya Chen', initials: 'MC', tone: 'bg-bv-blue-wash text-bv-blue' },
  sofia: { name: 'Sofia Alvarez', initials: 'SA', tone: 'bg-[hsl(265_80%_95%)] text-[hsl(265_50%_45%)]' },
  daniel: { name: 'Daniel Okafor', initials: 'DO', tone: 'bg-bv-orange-soft text-bv-coral' },
  priya: { name: 'Priya Nair', initials: 'PN', tone: 'bg-[hsl(42_95%_90%)] text-[hsl(32_80%_32%)]' },
  leo: { name: 'Leo Martins', initials: 'LM', tone: 'bg-bv-blue-wash text-bv-blue' },
  tom: { name: 'Tom Becker', initials: 'TB', tone: 'bg-bv-success-soft text-bv-success' },
};

/* ───────────── Result cards ───────────── */

function CardShell({ title, children, className }: { title: string; children: ReactNode; className?: string }) {
  return (
    <div className={cn('overflow-hidden rounded-2xl border border-bv-blue-line bg-white shadow-bv-card', className)}>
      <div className="flex items-center justify-between gap-3 border-b border-bv-blue-line bg-bv-blue-wash/50 px-3.5 py-2">
        <p className="truncate text-[12px] font-semibold text-bv-ink">{title}</p>
        <span className="inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-bv-blue">Open in Beatview <ExternalLink className="h-3 w-3" /></span>
      </div>
      <div className="p-3.5">{children}</div>
    </div>
  );
}

function JobCard() {
  return (
    <CardShell title="Senior Account Manager">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="rounded-full bg-bv-success-soft px-2 py-0.5 text-[11px] font-semibold text-bv-success">● Published</span>
        {['London', 'Hybrid', 'Full-time', '£55k–65k / year'].map(tag => <span key={tag} className="rounded-full border border-bv-blue-line px-2 py-0.5 text-[11px] font-medium text-bv-ink2">{tag}</span>)}
      </div>
      <div className="mt-3 flex items-center gap-2 rounded-xl border border-bv-blue-line bg-bv-base px-3 py-2">
        <Link2 className="h-3.5 w-3.5 shrink-0 text-bv-blue" />
        <span className="min-w-0 flex-1 truncate font-mono text-[11.5px] text-bv-ink">careers.youragency.com/jobs/senior-account-manager</span>
        <Copy className="h-3.5 w-3.5 shrink-0 text-bv-mute" />
      </div>
      <p className="mt-3 rounded-xl bg-bv-blue-wash/60 px-3 py-2 text-[12px] leading-5 text-bv-ink2">“We’re hiring a Senior Account Manager in London (hybrid). Own a portfolio of enterprise clients and grow renewals. Apply in two minutes →”</p>
    </CardShell>
  );
}

function RankedCard() {
  const rows = [
    { ...people.aisha, resume: 91, status: 'Interviewed · 88', ok: true },
    { ...people.maya, resume: 89, status: 'Interviewed · 84', ok: true },
    { ...people.sofia, resume: 84, status: 'Interviewed · 79', ok: true },
    { ...people.daniel, resume: 82, status: 'Invited · not started', ok: false },
    { ...people.priya, resume: 68, status: 'Below threshold', ok: false },
  ];
  return (
    <CardShell title="New applicants · Senior Account Manager">
      <table className="w-full text-left text-[12px]">
        <thead><tr className="text-[10.5px] uppercase tracking-wider text-bv-mute"><th className="pb-2 font-semibold">Candidate</th><th className="pb-2 font-semibold">Resume</th><th className="hidden pb-2 font-semibold sm:table-cell">AI interview</th></tr></thead>
        <tbody>
          {rows.map(row => (
            <tr key={row.name} className="border-t border-bv-blue-line">
              <td className="py-1.5"><span className="flex items-center gap-2"><Avatar initials={row.initials} tone={row.tone} /><span className="truncate font-semibold text-bv-ink">{row.name}</span></span></td>
              <td className="py-1.5"><span className={cn('rounded-md px-1.5 py-0.5 font-semibold tabular-nums', row.resume >= 80 ? 'bg-bv-success-soft text-bv-success' : 'bg-bv-orange-soft text-bv-coral')}>{row.resume}</span></td>
              <td className={cn('hidden py-1.5 sm:table-cell', row.ok ? 'text-bv-ink2' : 'text-bv-mute')}>{row.status}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </CardShell>
  );
}

function CompareCard() {
  const cols = [
    { ...people.aisha, score: 90, best: 'Renewal turnaround story with numbers', watch: 'Light on team leadership' },
    { ...people.maya, score: 87, best: 'Closest sector match — SaaS, 6 yrs', watch: 'Q3 answer was vague on targets' },
    { ...people.sofia, score: 82, best: 'Clear, structured communicator', watch: 'Smaller account sizes so far' },
  ];
  return (
    <CardShell title="Comparison · strongest first">
      <div className="grid grid-cols-3 gap-2">
        {cols.map((c, i) => (
          <div key={c.name} className={cn('rounded-xl border p-2.5', i === 0 ? 'border-bv-blue/40 bg-bv-blue-wash/50' : 'border-bv-blue-line')}>
            <div className="flex items-center justify-between gap-1">
              <Avatar initials={c.initials} tone={c.tone} />
              <ScoreRing score={c.score} size={36} stroke={3.5} tone={c.score >= 85 ? 'success' : 'blue'} />
            </div>
            <p className="mt-2 truncate text-[12px] font-semibold text-bv-ink">{c.name}</p>
            <p className="mt-2 text-[10px] font-semibold uppercase tracking-wider text-bv-success">Strength</p>
            <p className="text-[11px] leading-4 text-bv-ink2">{c.best}</p>
            <p className="mt-2 text-[10px] font-semibold uppercase tracking-wider text-bv-coral">Watch</p>
            <p className="text-[11px] leading-4 text-bv-ink2">{c.watch}</p>
          </div>
        ))}
      </div>
    </CardShell>
  );
}

function PipelineCard() {
  const columns = [
    { stage: 'New', count: 9, people: [people.priya] },
    { stage: 'Shortlisted', count: 6, people: [people.leo] },
    { stage: 'AI interview', count: 5, people: [people.daniel], flag: true },
    { stage: 'Ranked', count: 4, people: [people.aisha] },
    { stage: 'Face-to-face', count: 1, people: [people.tom] },
  ];
  return (
    <CardShell title="Pipeline · Senior Account Manager">
      <div className="grid grid-cols-5 gap-1.5">
        {columns.map(col => (
          <div key={col.stage} className={cn('min-w-0 rounded-xl p-1.5 sm:p-2', col.flag ? 'bg-bv-orange-soft/70 ring-1 ring-bv-coral/30' : 'bg-bv-blue-wash/60')}>
            <p className="truncate text-[10px] font-semibold text-bv-ink2 sm:text-[11px]">{col.stage}</p>
            <p className="font-display text-lg font-semibold tabular-nums text-bv-ink">{col.count}</p>
            {col.people.map(p => (
              <div key={p.name} className="mt-1 flex items-center gap-1 rounded-lg bg-white p-1 shadow-sm">
                <Avatar initials={p.initials} tone={p.tone} />
                <span className="hidden truncate text-[10px] font-medium text-bv-ink lg:inline">{p.name.split(' ')[0]}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </CardShell>
  );
}

function ConfirmCard({ confirmed, pressing, completed }: { confirmed: boolean; pressing: boolean; completed: boolean }) {
  const actions = [
    { who: people.aisha, what: 'Move to Face-to-face' },
    { who: people.daniel, what: 'Resend AI interview invitation' },
  ];
  return (
    <div className="overflow-hidden rounded-2xl border border-bv-blue-line bg-white shadow-bv-card">
      <ul className="divide-y divide-bv-blue-line">
        {actions.map(a => (
          <li key={a.what} className="flex items-center gap-2.5 px-3.5 py-2.5">
            <Avatar initials={a.who.initials} tone={a.who.tone} />
            <span className="min-w-0 flex-1 text-[12.5px] text-bv-ink"><span className="font-semibold">{a.who.name}</span> <ChevronRight className="inline h-3 w-3 text-bv-mute" /> {a.what}</span>
            {completed && <Check className="h-4 w-4 shrink-0 text-bv-success" strokeWidth={3} />}
          </li>
        ))}
      </ul>
      <div className="flex gap-2 border-t border-bv-blue-line bg-bv-base px-3.5 py-2.5">
        <span className={cn('rounded-lg px-3 py-1.5 text-[12px] font-semibold text-white transition-all duration-200', confirmed ? 'bg-bv-success' : 'bg-bv-ink', pressing && 'scale-95 ring-4 ring-bv-blue/25')}>{confirmed ? 'Confirmed' : 'Confirm'}</span>
        <span className="rounded-lg border border-bv-blue-line bg-white px-3 py-1.5 text-[12px] font-semibold text-bv-ink2">Cancel</span>
      </div>
    </div>
  );
}

function ResultCard({ scene, phase }: { scene: Scene; phase: Phase }) {
  switch (scene.card) {
    case 'job': return <JobCard />;
    case 'ranked': return <RankedCard />;
    case 'compare': return <CompareCard />;
    case 'pipeline': return <PipelineCard />;
    case 'confirm': return <ConfirmCard confirmed={phase === 'tools' || phase === 'done'} pressing={phase === 'confirm'} completed={phase === 'done'} />;
  }
}

/* ───────────── Chat pieces ───────────── */

function ToolChips({ tools, done, dark }: { tools: string[]; done: number; dark?: boolean }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {tools.map((tool, i) => {
        const finished = i < done;
        return (
          <motion.span key={tool} initial={{ opacity: 0, y: 4 }} animate={{ opacity: i <= done ? 1 : 0.35, y: 0 }} transition={{ duration: 0.25 }} className={cn('inline-flex items-center gap-1.5 rounded-lg border px-2 py-1 font-mono text-[11px]', dark ? 'border-white/10 bg-white/5 text-white/80' : 'border-bv-blue-line bg-bv-base text-bv-ink2')}>
            <Plug className="h-3 w-3 text-bv-blue" />
            <span className="font-sans font-semibold text-bv-mute">Beatview</span>
            {tool}
            {finished ? <Check className="h-3 w-3 text-bv-success" strokeWidth={3} /> : <Loader2 className="h-3 w-3 animate-spin text-bv-blue" />}
          </motion.span>
        );
      })}
    </div>
  );
}

function BeatviewMark() {
  return (
    <span className="relative flex h-7 w-7 shrink-0 items-center justify-center">
      <span className="absolute inset-0 rounded-full bg-[conic-gradient(from_0deg,hsl(var(--hero-highlight-start)),hsl(var(--blue)),hsl(var(--hero-highlight-end)),hsl(var(--hero-highlight-start)))]" />
      <span className="absolute inset-[2.5px] rounded-full bg-white" />
      <Sparkles className="relative h-3.5 w-3.5 text-bv-ink" />
    </span>
  );
}

/* ───────────── Demo ───────────── */

export function AssistantDemo({ compact = false, tone = 'light', initialClient = 'Claude', className }: { compact?: boolean; tone?: 'light' | 'dark'; initialClient?: McpClient; className?: string }) {
  const reduced = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { margin: '-15% 0px' });
  const [client, setClient] = useState<McpClient>(initialClient);
  const [sceneIndex, setSceneIndex] = useState(0);
  const [phase, setPhase] = useState<Phase>('reply');
  const [typed, setTyped] = useState(Number.MAX_SAFE_INTEGER);
  const [toolsDone, setToolsDone] = useState(Number.MAX_SAFE_INTEGER);
  const [playing, setPlaying] = useState(false);
  const [run, setRun] = useState(0);
  const tabsRef = useRef<HTMLDivElement>(null);
  const scene = MCP_SCENES[sceneIndex];

  useEffect(() => { if (inView && !reduced) setPlaying(true); }, [inView, reduced]);

  // On narrow screens the scene picker scrolls sideways; keep the active scene in view without moving the page.
  useEffect(() => {
    const strip = tabsRef.current;
    const tab = strip?.children[sceneIndex] as HTMLElement | undefined;
    if (!strip || !tab || strip.scrollWidth <= strip.clientWidth) return;
    strip.scrollTo({ left: tab.offsetLeft - strip.offsetLeft - 16, behavior: reduced ? 'auto' : 'smooth' });
  }, [sceneIndex, reduced]);

  const start = (index: number) => {
    setSceneIndex(index);
    setRun(r => r + 1);
    if (reduced) { setPhase(MCP_SCENES[index].card === 'confirm' ? 'done' : 'reply'); setTyped(Number.MAX_SAFE_INTEGER); setToolsDone(Number.MAX_SAFE_INTEGER); return; }
    setPhase('type');
    setTyped(0);
    setToolsDone(0);
  };

  // One timer drives the whole sequence: typing → tool calls → reply (→ confirm → tools → done) → hold → next scene.
  useEffect(() => {
    if (!playing || !inView) return;
    let id: number;
    if (phase === 'type' && typed < scene.prompt.length) {
      id = window.setTimeout(() => setTyped(t => t + 2), CHAR_MS);
    } else if (phase === 'tools' && toolsDone < scene.tools.length) {
      id = window.setTimeout(() => setToolsDone(d => d + 1), TOOL_MS);
    } else {
      const next = nextPhase(scene, phase);
      const wait = next === null || phase === 'done' ? HOLD_MS : phase === 'reply' ? 1600 : phase === 'confirm' ? 700 : phase === 'type' ? 350 : 250;
      id = window.setTimeout(() => {
        if (next === null || phase === 'done') start((sceneIndex + 1) % MCP_SCENES.length);
        else { if (next === 'tools') setToolsDone(0); setPhase(next); }
      }, wait);
    }
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, inView, phase, typed, toolsDone, sceneIndex]);

  const promptText = scene.prompt.slice(0, typed);
  const promptDone = phase !== 'type';
  const showTools = scene.card === 'confirm' ? phase === 'tools' || phase === 'done' : phase !== 'type';
  const showReply = scene.card === 'confirm' ? phase !== 'type' : phase === 'reply';
  const toolCount = Math.min(toolsDone, scene.tools.length);
  const placeholder = client === 'ChatGPT' ? 'Ask anything' : client === 'Gemini' ? 'Ask Gemini' : client === 'Cursor' ? 'Plan, search, build anything' : 'Reply…';

  return (
    <div ref={ref} className={cn('relative', className)} data-prerender="skip">
      <div className={cn('grid gap-4', !compact && 'lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-6')}>
        {/* Scene picker */}
        <div ref={tabsRef} role="tablist" aria-label="Example requests" className={cn('flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none]', !compact && 'lg:flex-col lg:overflow-visible lg:pb-0')}>
          {MCP_SCENES.map((s, i) => {
            const active = i === sceneIndex;
            return (
              <button key={s.id} type="button" role="tab" aria-selected={active} onClick={() => { setPlaying(!reduced); start(i); }} className={cn('group relative shrink-0 overflow-hidden rounded-xl border px-3.5 py-2.5 text-left text-[13px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bv-blue', tone === 'dark' ? (active ? 'border-white bg-white text-bv-ink' : 'border-white/15 bg-white/[0.06] text-white/70 hover:border-white/30 hover:text-white') : (active ? 'border-bv-ink bg-bv-ink text-white' : 'border-bv-blue-line bg-white/80 text-bv-ink2 hover:border-bv-blue/40 hover:text-bv-ink'), !compact && 'lg:py-3')}>
                <span className={cn('mr-2 font-mono text-[11px]', active !== (tone === 'dark') ? 'text-white/50' : 'text-bv-mute')}>0{i + 1}</span>{s.label}
                {active && playing && !reduced && (
                  <motion.span key={run} aria-hidden className="bv-btn-gradient absolute bottom-0 left-0 h-[3px]" initial={{ width: '0%' }} animate={{ width: '100%' }} transition={{ duration: (run === 0 ? HOLD_MS : sceneDuration(s)) / 1000, ease: 'linear' }} />
                )}
              </button>
            );
          })}
        </div>

        {/* Chat window */}
        <div className="overflow-hidden rounded-[24px] border border-white bg-white/95 shadow-[0_50px_120px_-50px_hsl(var(--navy)/0.55)] ring-1 ring-bv-blue-line backdrop-blur-xl">
          <div className="flex items-center gap-3 border-b border-bv-blue-line bg-bv-blue-wash/70 px-4 py-2.5">
            <div className="flex gap-1.5" aria-hidden><span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]" /><span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]" /><span className="h-2.5 w-2.5 rounded-full bg-[#28C840]" /></div>
            <div role="group" aria-label="AI assistant" className="flex rounded-lg border border-bv-blue-line bg-white p-0.5">
              {MCP_CLIENTS.map(name => (
                <button key={name} type="button" aria-pressed={client === name} onClick={() => setClient(name)} className={cn('rounded-md px-2.5 py-1 text-[12px] font-semibold transition-colors', client === name ? 'bg-bv-ink text-white' : 'text-bv-ink2 hover:text-bv-ink')}>{name}</button>
              ))}
            </div>
            <span className="ml-auto hidden shrink-0 rounded-md bg-bv-orange-soft px-2 py-0.5 text-[11px] font-semibold text-bv-coral sm:inline">Illustration · example data</span>
          </div>

          <div className={cn('flex flex-col gap-4 overflow-hidden p-4 sm:p-5', compact ? 'h-[560px] sm:h-[440px]' : 'h-[580px] sm:h-[500px]')} aria-live="off">
            {/* User message */}
            <div className="flex justify-end">
              <div className="max-w-[88%] rounded-2xl rounded-br-md bg-bv-ink px-4 py-2.5 text-[14px] leading-6 text-white">
                {promptText}{!promptDone && <span className="ml-0.5 inline-block h-4 w-[2px] translate-y-0.5 animate-pulse bg-white/80" />}
              </div>
            </div>

            {/* Assistant turn */}
            <AnimatePresence mode="wait">
              {promptDone && (
                <motion.div key={scene.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.3, ease: EASE }} className="flex min-h-0 gap-3">
                  <BeatviewMark />
                  <div className="min-w-0 flex-1 space-y-3">
                    {scene.card !== 'confirm' && showTools && <ToolChips tools={scene.tools} done={toolCount} />}
                    {showReply && (
                      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: EASE }} className="space-y-3">
                        <p className="text-[14px] leading-6 text-bv-ink">{scene.reply}</p>
                        <ResultCard scene={scene} phase={phase} />
                      </motion.div>
                    )}
                    {scene.card === 'confirm' && showTools && <ToolChips tools={scene.tools} done={toolCount} />}
                    {scene.card === 'confirm' && phase === 'done' && (
                      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[14px] leading-6 text-bv-ink">{scene.done}</motion.p>
                    )}
                    {showReply && scene.card !== 'confirm' && (
                      <p className="flex items-center gap-1.5 text-[11.5px] text-bv-mute"><ShieldCheck className="h-3.5 w-3.5" />AI scores are signals to review — you make the decision.</p>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Composer */}
          <div className="border-t border-bv-blue-line bg-white px-4 py-3">
            <div className="flex items-center gap-2 rounded-xl border border-bv-blue-line bg-bv-base px-3 py-2">
              <span className="min-w-0 flex-1 truncate text-[13px] text-bv-mute">{placeholder}</span>
              <span className="hidden shrink-0 items-center gap-1.5 rounded-full border border-bv-blue-line bg-white px-2 py-0.5 text-[11px] font-semibold text-bv-ink2 sm:inline-flex"><span className="h-1.5 w-1.5 rounded-full bg-bv-success" />Beatview connected</span>
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-bv-ink text-white"><ArrowUp className="h-3.5 w-3.5" /></span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
